import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { recaptchaActions } from "../../../../../store/reducers/recaptcha";
import useApi from "../../../../../hooks/useApi";
import constants from "../../../../../utils/constants";
import BookingStep from "../BookingStep";
import InquirySubHeader from "./InquirySubHeader";
import InquiryClientDetails from "./InquiryClientDetails";
import ConsultSubHeader from "./ConsultSubHeader";
import ConsultClientDetails from "./ConsultClientDetails";
import LogoHeader from "../../../../UI/LogoHeader";
import ClientDetailsActions from "./ClientDetailsActions";
import InfoDialog from "./InfoDialog";

const validateInfo = (clientDetails, recaptchaToken, requestType, preQualified) => {
  let validInfo = clientDetails.firstName && clientDetails.lastName && recaptchaToken;
  const additionalInfo = clientDetails.additionalInfo;
  const meetingType = clientDetails.meetingType;

  if (requestType === constants.REQUEST_TYPES.INQUIRY) {
    const contactPreference = additionalInfo.contactPreference;
    validInfo = validInfo && contactPreference && additionalInfo.lawArea;

    if (
      meetingType === constants.MEETING_TYPES.IN_PERSON ||
      meetingType === constants.MEETING_TYPES.PHONE ||
      contactPreference === "phone" ||
      contactPreference === "noPreference"
    ) {
      return validInfo && clientDetails.phone;
    }

    if (meetingType === constants.MEETING_TYPES.VIRTUAL || contactPreference === "email") {
      return validInfo && clientDetails.email;
    }
  } else {
    let accidentValid = true;
    const numberOfOccupants = additionalInfo.numberOfOccupants;

    if (!preQualified && (!additionalInfo.accidentDate || !additionalInfo.description)) {
      accidentValid = false;
    }

    return (
      validInfo &&
      clientDetails.phone &&
      clientDetails.city &&
      accidentValid &&
      numberOfOccupants &&
      additionalInfo.driverOrPassenger
    );
  }
};

export default function ClientDetails() {
  const branchesInfo = useSelector((state) => state.booking.branchesInfo);
  const requestType = useSelector((state) => state.booking.requestType);
  const initialClientDetails = { ...useSelector((state) => state.booking.clientDetails) };
  const recaptchaToken = useSelector((state) => state.recaptcha.token);
  const preQualified = useSelector((state) => state.booking.bookingStatus) === constants.BOOKING_STATUS.PRE_QUALIFIED;

  const [clientDetails, setClientDetails] = useState(initialClientDetails);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const {
    isLoading: sendingRequest,
    sendRequest,
    error,
  } = useApi({
    successMessage: "Meeting request sent and saved successfully",
    errorMessage: "Error sending meeting request",
    liveErrors: true,
  });

  const additionalInfo = clientDetails.additionalInfo;
  const meetingType = clientDetails.meetingType;
  const validInfo = validateInfo(clientDetails, recaptchaToken, requestType, preQualified);

  const dispatch = useDispatch();

  const clientDetailsChangeHandler = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    if (name === "phone") {
      value = value.replaceAll(/[^0-9]/g, "");
    }

    if (name === "email") {
      value = value.replaceAll(/[ ]/g, "");
    }

    setClientDetails((prevState) => ({ ...prevState, [name]: value }));
  };

  const infoDialogOpenHandler = () => {
    setInfoDialogOpen(true);
  };

  const infoDialogCloseHandler = () => {
    setInfoDialogOpen(false);
  };

  const confirmDetails = (event, callback) => {
    event.preventDefault();

    let currentPostalCode = clientDetails.postalCode;

    //If postal code is in (OCB) and it is an CA postal code, format it with a space before sending to DB
    if (currentPostalCode && currentPostalCode.length > 5) {
      currentPostalCode = currentPostalCode.replace(/[- ]/, "");
      currentPostalCode = currentPostalCode.slice(0, 3) + " " + currentPostalCode.slice(3);
      clientDetails.postalCode = currentPostalCode;
    }

    if (validInfo) {
      if (recaptchaToken) {
        sendRequest(
          {
            url: "api/booking-app/meeting-request.php",
            method: "POST",
            payload: { ...clientDetails, requestType, preQualified, recaptchaToken },
          },
          callback
        );
      } else {
        dispatch(recaptchaActions.updateError("Please, answer the reCAPTCHA first"));
      }
    } else {
      dispatch(recaptchaActions.updateError("Invalid information. Please try again"));
    }
  };

  useEffect(() => {
    if (error) {
      dispatch(recaptchaActions.updateReset(true));
    }
  }, [dispatch, error]);

  return requestType === constants.REQUEST_TYPES.INQUIRY ? (
    <BookingStep
      header={<LogoHeader title="Details" display={{ xs: "block", sm: "none" }} />}
      subheader={<InquirySubHeader lawArea={additionalInfo.lawArea} meetingDateTime={clientDetails.meetingDateTime} />}
      sm={11}
      md={9}
      lg={7}
      xl={6}
    >
      <InquiryClientDetails
        brancheInfo={branchesInfo[clientDetails.selectedBranch]}
        clientDetails={clientDetails}
        recaptchaToken={recaptchaToken}
        onClientDetailsChange={clientDetailsChangeHandler}
        onClientDetailsChangeCustom={setClientDetails}
        onInfoOpen={infoDialogOpenHandler}
        onConfirmDetails={confirmDetails}
        actions={<ClientDetailsActions loading={sendingRequest} validInfo={validInfo} meetingType={meetingType} />}
      />
    </BookingStep>
  ) : (
    <>
      <BookingStep
        header={<LogoHeader title="Details" display={{ xs: "block", sm: "none" }} />}
        subheader={
          <ConsultSubHeader
            meetingType={meetingType}
            branchInfo={branchesInfo[clientDetails.selectedBranch]}
            meetingDateTime={clientDetails.meetingDateTime}
          />
        }
        xs={12}
        md={11}
        lg={8}
        xl={7}
      >
        <ConsultClientDetails
          brancheInfo={branchesInfo[clientDetails.selectedBranch]}
          clientDetails={clientDetails}
          recaptchaToken={recaptchaToken}
          onClientDetailsChange={clientDetailsChangeHandler}
          onClientDetailsChangeCustom={setClientDetails}
          onInfoOpen={infoDialogOpenHandler}
          onConfirmDetails={confirmDetails}
          actions={
            <ClientDetailsActions
              loading={sendingRequest}
              validInfo={validInfo}
              onInfoDialogOpen={infoDialogOpenHandler}
              meetingType={meetingType}
            />
          }
        />
      </BookingStep>

      {infoDialogOpen && <InfoDialog open={infoDialogOpen} onClose={infoDialogCloseHandler} />}
    </>
  );
}
